/**
 * src/game/weapons/weapon-loot.ts — R-005 P3 主武器取得(掉落 + 寶箱 + 打魔王)。
 *
 * 讓 6 把「行為主武器」(registry.ts)真的靠玩法取得,而不是預設全解鎖:
 *   B. 打魔王 → 首殺給一把行為主武器(BOSS_MAIN_WEAPON 對應表)。
 *   C. 戰場武器箱(單場)→ 擊殺機率掉,撿到當場換武器(不寫永久 selectedWeapon)。
 *   D. 戰後寶箱(永久)→ 勝利機率永久解鎖一把「尚未擁有」的行為主武器。
 *
 * ── 鐵則(WEAPON_SYSTEM_SPEC §0 鐵則放寬 + 學習/遊戲分離)──
 *   全程玩遊戲軌:只讀/寫 unlockedWeapons / selectedWeapon(廣度),**絕不**碰
 *   factionXP / freeXP / skillLevels / 修煉值。武器威力 = 武器等級曲線,非戰力。
 *   本檔 = 純資料 + 純函式(無 Phaser / DOM / progress import),可單元測試;
 *   隨機性以注入的 rng() 表達(預設 Math.random),測試餵固定值斷言邊界。
 *
 * 注意:此處的「主武器」= registry.ts 的 6 把 behavior 武器(rapid-cannon/railgun/
 * homing-missile/spin-blade/laser-beam/mortar),**不是** boss-weapon-unlocks.ts 的
 * 15 把 `weapon-*` 單場 RunModifier 改造卡(那條另走、各自獨立)。
 */

import { RAPID_CANNON_ID } from './rapid-cannon';
import { RAILGUN_ID } from './railgun';
import { HOMING_MISSILE_ID } from './homing-missile';
import { SPIN_BLADE_ID } from './spin-blade';
import { LASER_BEAM_ID } from './laser-beam';
import { MORTAR_ID } from './mortar';

/** 武器稀有度:越稀有 → 掉率越低、玩法越特別。 */
export type WeaponRarity = 'common' | 'rare' | 'epic';

/**
 * 起手就有的行為主武器(預設解鎖,不靠掉落)。
 * rapid-cannon = 永遠可用的預設;railgun = 第二把起手,讓新玩家一開始就有一把
 * 「動詞差很大」的可選武器(蓄力),其餘 4 把靠打魔王 / 掉落 / 寶箱取得。
 */
export const STARTER_MAIN_WEAPONS: readonly string[] = [RAPID_CANNON_ID, RAILGUN_ID];

/** 一把行為主武器的掉落屬性(稀有度)。 */
export interface MainWeaponLootMeta {
  id: string;
  rarity: WeaponRarity;
}

/**
 * 6 把行為主武器的稀有度表(掉落 / 寶箱權重用)。
 * - common:好上手(連發機砲 / 追蹤飛彈 = 射後不理)。
 * - rare:有點深度(軌道炮蓄力 / 榴彈炮預判)。
 * - epic:最特別(雷射掃描過熱 / 旋刃近戰)。
 */
export const MAIN_WEAPON_LOOT: readonly MainWeaponLootMeta[] = [
  { id: RAPID_CANNON_ID, rarity: 'common' },
  { id: HOMING_MISSILE_ID, rarity: 'common' },
  { id: RAILGUN_ID, rarity: 'rare' },
  { id: MORTAR_ID, rarity: 'rare' },
  { id: LASER_BEAM_ID, rarity: 'epic' },
  { id: SPIN_BLADE_ID, rarity: 'epic' },
];

/** 查一把行為主武器的稀有度(未知 → 'common' 當保底)。 */
export function weaponRarity(weaponId: string): WeaponRarity {
  return MAIN_WEAPON_LOOT.find((w) => w.id === weaponId)?.rarity ?? 'common';
}

// ── B. 打魔王 → 首殺給一把行為主武器 ─────────────────────────────────────────
//
// 把 6 把行為主武器對應到 6 隻代表性魔王(各 tier 的門面)。打贏該魔王首殺除了
// 既有 boss-weapon-unlocks(weapon-* 單場改造卡)外,**另**永久解鎖這把行為主武器。
// 起手就有的(rapid-cannon/railgun)也列出 → recordWeaponUnlocked 冪等,已擁有不再彈。
//
// 此處硬寫 boss id(對齊 boss-weapon-unlocks.ts 的 15 隻清單),不 import boss-pool,
// 避免耦合魔王平衡數值。沒列到的 boss → 該隻只給 weapon-* 改造卡(行為主武器 = null)。
export const BOSS_MAIN_WEAPON: Readonly<Record<string, string>> = {
  'shu-mo': RAPID_CANNON_ID,       // 數魔・乘九(Tier 1 門面)→ 連發機砲(起手,冪等)
  'fu-hun': HOMING_MISSILE_ID,     // 符魂・舌劍 → 追蹤飛彈(射後不理)
  'yuan-su': RAILGUN_ID,           // 元素守衛 → 軌道炮(蓄力,起手,冪等)
  'shi-zhong': MORTAR_ID,          // 期中總魔 → 榴彈炮(預判潑群)
  'tian-ming': LASER_BEAM_ID,      // 期末王 → 雷射光束(掃描,期末大魔王 = epic)
  'shu-fu': SPIN_BLADE_ID,         // 數符雙煞 → 旋刃(近戰,epic)
};

/**
 * 打贏某隻魔王應額外解鎖的「行為主武器」id(查不到 → null)。
 * 純函式:給 result-scene 在 boss 首殺時查「該額外給哪把主武器」。
 */
export function mainWeaponForBoss(bossId: string | null | undefined): string | null {
  if (!bossId) return null;
  return BOSS_MAIN_WEAPON[bossId] ?? null;
}

// ── C. 戰場武器箱(單場)掉落 ─────────────────────────────────────────────────
//
// 擊殺敵人時機率掉「武器箱」pickup。撿到當場立即換成箱內武器(單場,run 結束不改
// 永久 selectedWeapon)。稀有度決定掉哪把:多數掉 common,少數 rare,極少 epic。

/** 一般敵人擊殺掉武器箱的基礎機率(精英/小魔王再乘倍率)。 */
export const WEAPON_BOX_DROP_CHANCE = 0.02;
/** 精英 / 小魔王擊殺的武器箱掉率倍率(更容易掉 → 鼓勵打精英)。 */
export const WEAPON_BOX_ELITE_MULT = 4;

/**
 * 擲一次「這次擊殺要不要掉武器箱」。
 * @param isElite 精英 / 小魔王(掉率 × WEAPON_BOX_ELITE_MULT)。
 * @param rng 0..1 亂數(預設 Math.random;測試餵固定值)。
 * 純函式;回傳 true = 掉箱。
 */
export function rollWeaponBoxDrop(isElite: boolean, rng: () => number = Math.random): boolean {
  const chance = WEAPON_BOX_DROP_CHANCE * (isElite ? WEAPON_BOX_ELITE_MULT : 1);
  return rng() < Math.min(1, chance);
}

/** 武器箱掉哪把(依稀有度權重):common 多、rare 中、epic 少。 */
const BOX_RARITY_WEIGHT: Readonly<Record<WeaponRarity, number>> = {
  common: 6,
  rare: 3,
  epic: 1,
};

/**
 * 從 6 把行為主武器(依稀有度權重)挑一把放進武器箱。
 * @param rng 0..1 亂數(預設 Math.random)。
 * 純函式;回傳武器 id(權重抽樣,稀有的少出現)。永遠回一把(不可能空)。
 */
export function pickWeaponBoxContents(rng: () => number = Math.random): string {
  let total = 0;
  for (const w of MAIN_WEAPON_LOOT) total += BOX_RARITY_WEIGHT[w.rarity];
  let roll = Math.min(Math.max(rng(), 0), 0.9999999) * total;
  for (const w of MAIN_WEAPON_LOOT) {
    roll -= BOX_RARITY_WEIGHT[w.rarity];
    if (roll < 0) return w.id;
  }
  // 理論上到不了(權重和 > 0);保底回第一把。
  return MAIN_WEAPON_LOOT[0].id;
}

// ── D. 戰後寶箱(永久)解鎖 ───────────────────────────────────────────────────
//
// 勝利結算機率開出寶箱,從「尚未擁有」的行為主武器永久解鎖一把(union 進
// unlockedWeapons)。已全收集 → 寶箱不開武器(回 null,結算不彈)。

/** 戰後寶箱開出新武器的基礎機率(打贏魔王再加成,見 victoryChestUnlock)。 */
export const VICTORY_CHEST_CHANCE = 0.25;
/** 打贏魔王時戰後寶箱的額外機率加成(魔王戰更可能掉新武器)。 */
export const VICTORY_CHEST_BOSS_BONUS = 0.35;

/**
 * 「尚未擁有」的行為主武器清單(registry 全部 − 已解鎖 − 起手永遠有的)。
 * 純函式;給 victoryChestUnlock 挑要解鎖哪把,也給「是否已全收集」判定。
 */
export function unownedMainWeapons(unlockedWeapons: readonly string[] | null | undefined): string[] {
  const owned = new Set<string>(STARTER_MAIN_WEAPONS);
  for (const id of unlockedWeapons ?? []) owned.add(id);
  return MAIN_WEAPON_LOOT.map((w) => w.id).filter((id) => !owned.has(id));
}

/**
 * 擲一次戰後寶箱:回傳「這次永久解鎖哪把行為主武器」或 null(沒開出 / 已全收集)。
 * 鐵則:只回 id 讓 caller 寫 unlockedWeapons,本函式不碰 progress。
 *
 * @param unlockedWeapons 目前已解鎖(判定還缺哪些)。
 * @param bossDefeated 本場是否打贏魔王(true → 機率加成)。
 * @param rng 0..1 亂數(預設 Math.random;測試餵固定值斷言邊界)。
 * 純函式;回傳要解鎖的武器 id,或 null。
 */
export function victoryChestUnlock(
  unlockedWeapons: readonly string[] | null | undefined,
  bossDefeated: boolean,
  rng: () => number = Math.random,
): string | null {
  const pool = unownedMainWeapons(unlockedWeapons);
  if (pool.length === 0) return null; // 已全收集 → 寶箱不開武器。
  const chance = Math.min(1, VICTORY_CHEST_CHANCE + (bossDefeated ? VICTORY_CHEST_BOSS_BONUS : 0));
  // 第一擲:有沒有開出武器。
  if (rng() >= chance) return null;
  // 第二擲:從尚未擁有的挑一把(均勻 — 缺的都珍貴,不再分稀有度權重)。
  const idx = Math.min(pool.length - 1, Math.floor(Math.min(Math.max(rng(), 0), 0.9999999) * pool.length));
  return pool[idx];
}
